import { useState, useEffect, useCallback } from 'react';
import { X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, RotateCw, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PhotoLightbox = ({ 
  photos = [], 
  initialIndex = 0, 
  isOpen, 
  onClose,
  title = 'Photo Gallery'
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setZoom(1);
      setRotation(0);
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen, initialIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
          handleZoomOut();
          break;
        case 'r':
          handleRotate();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, currentIndex, photos.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
    resetView();
  }, [photos.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
    resetView();
  }, [photos.length]);

  const resetView = () => {
    setZoom(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.5, 4));
  };

  const handleZoomOut = () => {
    setZoom((prev) => {
      const newZoom = Math.max(prev - 0.5, 0.5);
      if (newZoom === 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleDownload = () => {
    const currentPhoto = photos[currentIndex];
    const photoUrl = typeof currentPhoto === 'string' ? currentPhoto : currentPhoto?.url || currentPhoto?.dataUrl;
    
    if (photoUrl) {
      const link = document.createElement('a');
      link.href = photoUrl;
      link.download = `photo-${currentIndex + 1}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || photos.length === 0) return null;

  const currentPhoto = photos[currentIndex];
  const photoUrl = typeof currentPhoto === 'string' ? currentPhoto : currentPhoto?.url || currentPhoto?.dataUrl;
  const photoName = typeof currentPhoto === 'object' ? currentPhoto?.name : `Photo ${currentIndex + 1}`;

  return (
    <div 
      className="fixed inset-0 bg-black/95 z-[9999] flex flex-col"
      onClick={handleBackdropClick}
      data-testid="photo-lightbox"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      {/* Header - with safe area padding for mobile notch/status bar */}
      <div className="flex items-center justify-between p-4 pt-[max(1rem,env(safe-area-inset-top))] bg-black/50 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <h3 className="text-white font-semibold text-lg">{title}</h3>
          <span className="text-white/70 text-sm">
            {currentIndex + 1} of {photos.length}
          </span>
        </div>
        
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Zoom Controls - simplified on mobile */}
          <div className="hidden sm:flex items-center gap-1 bg-white/10 rounded-lg px-2 py-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleZoomOut}
              className="h-8 w-8 text-white hover:bg-white/20"
              disabled={zoom <= 0.5}
              data-testid="zoom-out-btn"
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-white text-sm min-w-[50px] text-center">
              {Math.round(zoom * 100)}%
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleZoomIn}
              className="h-8 w-8 text-white hover:bg-white/20"
              disabled={zoom >= 4}
              data-testid="zoom-in-btn"
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
          </div>

          {/* Mobile zoom buttons */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleZoomOut}
            className="sm:hidden h-10 w-10 text-white hover:bg-white/20"
            disabled={zoom <= 0.5}
          >
            <ZoomOut className="w-5 h-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleZoomIn}
            className="sm:hidden h-10 w-10 text-white hover:bg-white/20"
            disabled={zoom >= 4}
          >
            <ZoomIn className="w-5 h-5" />
          </Button>

          {/* Rotate Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRotate}
            className="h-10 w-10 text-white hover:bg-white/20"
            title="Rotate (R)"
            data-testid="rotate-btn"
          >
            <RotateCw className="w-5 h-5" />
          </Button>

          {/* Download Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDownload}
            className="h-10 w-10 text-white hover:bg-white/20"
            title="Download"
            data-testid="download-btn"
          >
            <Download className="w-5 h-5" />
          </Button>

          {/* Close Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-10 w-10 text-white hover:bg-white/20"
            data-testid="close-lightbox-btn"
          >
            <X className="w-6 h-6" />
          </Button>
        </div>
      </div>

      {/* Main Image Area */}
      <div 
        className="flex-1 flex items-center justify-center overflow-hidden relative"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
      >
        {/* Previous Button */}
        {photos.length > 1 && (
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
            className="absolute left-4 z-10 h-14 w-14 rounded-full bg-black/50 text-white hover:bg-black/70"
            data-testid="prev-photo-btn"
          >
            <ChevronLeft className="w-8 h-8" />
          </Button>
        )}

        {/* Image */}
        <img
          src={photoUrl}
          alt={photoName}
          className="max-w-full max-h-full object-contain transition-transform duration-200 select-none"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${zoom}) rotate(${rotation}deg)`,
            transformOrigin: 'center center'
          }}
          draggable={false}
          data-testid="lightbox-image"
        />

        {/* Next Button */}
        {photos.length > 1 && (
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            className="absolute right-4 z-10 h-14 w-14 rounded-full bg-black/50 text-white hover:bg-black/70"
            data-testid="next-photo-btn"
          >
            <ChevronRight className="w-8 h-8" />
          </Button>
        )}
      </div>

      {/* Footer - Thumbnail Strip */}
      {photos.length > 1 && (
        <div className="p-4 bg-black/50 backdrop-blur-sm">
          <div className="flex items-center justify-center gap-2 overflow-x-auto max-w-full">
            {photos.map((photo, index) => {
              const thumbUrl = typeof photo === 'string' ? photo : photo?.url || photo?.dataUrl;
              return (
                <button
                  key={index}
                  onClick={() => { setCurrentIndex(index); resetView(); }}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    index === currentIndex 
                      ? 'border-white scale-110' 
                      : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                  data-testid={`thumbnail-${index}`}
                >
                  <img
                    src={thumbUrl}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              );
            })}
          </div>
          
          {/* Keyboard Hints */}
          <div className="flex items-center justify-center gap-4 mt-3 text-white/50 text-xs">
            <span>← → Navigate</span>
            <span>+/- Zoom</span>
            <span>R Rotate</span>
            <span>ESC Close</span>
          </div>
        </div>
      )}

      {/* Photo Name */}
      {photoName && (
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-lg text-sm">
          {photoName}
        </div>
      )}
    </div>
  );
};

export default PhotoLightbox;
